import { Controller } from "@hotwired/stimulus"

// Typeahead for remote branch names. As the user types, queries a JSON endpoint
// that returns `{ branches: [name, ...] }` and renders them as a clickable list.
// Clicking a result fills the input and clears the list.
//
// Markup:
//   <div data-controller="branch-search"
//        data-branch-search-url-value="/.../branches/search">
//     <input data-branch-search-target="input">
//     <div data-branch-search-target="results"></div>
//   </div>
//
// The endpoint receives the value as `?q=<value>`.
export default class extends Controller {
  static targets = ["input", "results"]
  static values = {
    url: String,
    param: { type: String, default: "q" },
    debounce: { type: Number, default: 250 },
  }

  connect() {
    if (!this.hasInputTarget) return
    this.timer = null
    this.boundSchedule = () => this.schedule()
    this.inputTarget.addEventListener("input", this.boundSchedule)
  }

  disconnect() {
    clearTimeout(this.timer)
    if (this.hasInputTarget && this.boundSchedule) {
      this.inputTarget.removeEventListener("input", this.boundSchedule)
    }
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.run(), this.debounceValue)
  }

  run() {
    const value = this.inputTarget.value.trim()
    if (!value) {
      this.clear()
      return
    }
    const url = `${this.urlValue}?${this.paramValue}=${encodeURIComponent(value)}`
    fetch(url)
      .then((r) => r.json())
      .then((d) => this.render(d.branches || []))
      .catch(() => {})
  }

  render(branches) {
    if (!this.hasResultsTarget) return
    if (!branches.length) {
      this.clear()
      return
    }
    this.resultsTarget.innerHTML = ""
    for (const name of branches) {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "list-group-item list-group-item-action"
      item.textContent = name
      item.addEventListener("click", () => this.choose(name))
      this.resultsTarget.appendChild(item)
    }
    this.resultsTarget.classList.add("list-group")
  }

  choose(name) {
    this.inputTarget.value = name
    this.clear()
  }

  clear() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.remove("list-group")
    }
  }
}
